<script setup>
import { computed } from "vue";
import { useI18n } from "vue-i18n";

const { plan, plansBenefits } = defineProps(["plan", "plansBenefits"]);
const emit = defineEmits(["closeModal", "createSinglePayment"]);

const { t } = useI18n();

// Safety check for plan data
const isValidPlan = computed(() => {
  return plan &&
         plan.id &&
         plan.name &&
         plan.currency_symbol &&
         (plan.unit_price !== undefined && plan.unit_price !== null);
});

// Format currency symbol - change PLN to zł
const formatCurrency = (currencySymbol) => {
  return currencySymbol === 'PLN' ? 'zł' : currencySymbol || '';
};

const createSinglePayment = async (plan, provider) => {
  emit("createSinglePayment", {
    plan: plan,
    provider: provider,
  });
};
</script>
<template>
  <div class="plan-card" :key="plan.id">
    <div class="plan-header">
      <h3 class="plan-title">{{ plan.name }}</h3>
      <div class="plan-price">
        <div class="price-main">
          <span class="price-amount">{{ plan.unit_price }}</span>
          <span class="price-currency">{{ plan.currency_symbol }}</span>
        </div>
        <div class="price-period">{{ t("billing.per_document") }}</div>
      </div>

      <!-- PROMO40 Banner -->
      <div class="promo-banner">
        <span class="promo-text">{{ t("header.promo_code_banner") }}</span>
      </div>
    </div>

    <div class="plan-features">
      <div class="features-list">
        <div class="feature" v-for="feature in plansBenefits" :key="feature">
          <span class="feature-icon">→</span>
          <span class="feature-text">{{ feature }}</span>
        </div>
      </div>
    </div>

    <div class="plan-actions">
      <button class="btn-primary" @click="() => createSinglePayment(plan.id, 'stripe')">
        {{ t("billing.buy_now").toUpperCase() }}
      </button>

      <div class="discount-section">
        <div class="discount-label">
          <span class="star-icon">★</span>
          {{ t("billing.crypto_discount") }}

        </div>
        <button class="btn-discount" @click="() => createSinglePayment(plan.id, 'vaiot')">
          {{ t("billing.choose") }}
        </button>
      </div>
    </div>
  </div>
</template>
<style scoped lang="scss">
.plan-card {
  width: 260px;
  min-height: 460px;
  background: transparent;
  border: 2px solid var(--booger-buster);
  border-radius: 16px;
  box-shadow: 0 4px 12px rgba(154, 185, 179, 0.1);
  padding: 24px 20px;
  position: relative;
  transition: all 0.2s ease;
  display: flex;
  flex-direction: column;

  &:hover {
    box-shadow: 0 8px 24px rgba(211, 240, 115, 0.3);
    transform: translateY(-2px);
    border-color: #c9e85a;
  }

  @media (max-width: 1200px) {
    width: 300px;
    padding: 24px;
    min-height: 480px;
  }

  @media (max-width: 1100px) {
    width: 280px;
    padding: 22px;
    min-height: 470px;
  }

  @media (max-width: 1000px) {
    width: 270px;
    padding: 20px;
    min-height: 460px;
  }

  @media (max-width: 900px) {
    width: 260px;
    padding: 20px;
    min-height: 450px;
  }

  @media (max-width: 820px) {
    width: 250px;
    padding: 18px;
    min-height: 440px;
  }

  @media (max-width: 950px) {
    width: 100%;
    max-width: 500px;
    min-height: auto;
    margin-bottom: 20px;
    padding: 32px;
  }

  @media (max-width: 768px) {
    padding: 24px;
  }
}

.plan-header {
  text-align: center;
  margin-bottom: 32px;
  margin-top: 12px;
  padding-bottom: 24px;
  border-bottom: 1px solid var(--light-grey);
}

.plan-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--eerie-black);
  margin: 0 0 12px 0;
  font-family: 'Archivo', sans-serif;
}

.plan-price {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  .price-main {
    display: flex;
    align-items: baseline;
    gap: 4px;
    margin-bottom: 4px;
  }

  .price-amount {
    font-size: 2rem;
    font-weight: 800;
    color: var(--eerie-black);
  }

  .price-currency {
    font-size: 1rem;
    font-weight: 600;
    color: #666;
    text-transform: uppercase;
  }

  .price-period {
    font-size: 0.875rem;
    color: #666;
    text-align: center;
    font-weight: 400;
  }
}

.promo-banner {
  background: var(--booger-buster);
  border: 1px solid #c9e85a;
  border-radius: 8px;
  padding: 8px 12px;
  margin-top: 16px;

  .promo-text {
    font-size: 0.75rem;
    font-weight: 600;
    color: var(--eerie-black);
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
}

.plan-features {
  flex: 1;
  margin-bottom: 32px;
}

.features-list {
  .feature {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 12px;
    font-size: 0.9375rem;
    color: var(--dark-grey);
    line-height: 1.4;

    &:last-child {
      margin-bottom: 0;
    }
  }

  .feature-icon {
    color: var(--booger-buster);
    font-weight: bold;
  }
}

.plan-actions {
  margin-top: auto;
}

.btn-primary {
  width: 100%;
  background: var(--blue-bonnet);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 12px 14px;
  font-size: 0.875rem;
  font-weight: 600;
  font-family: 'Archivo', sans-serif;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-bottom: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  &:hover:not(:disabled) {
    background: var(--egyptian-blue);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 35, 239, 0.3);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.discount-section {
  border: 1px solid var(--booger-buster);
  border-radius: 8px;
  padding: 12px;
  background: #f9fdf4;
}

.discount-label {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--eerie-black);
  margin-bottom: 10px;
  white-space: normal;
  word-break: break-word;
  text-align: center;
  line-height: 1.3;
}

.star-icon {
  color: var(--booger-buster);
  font-size: 0.9rem;
}

.btn-discount {
  width: 100%;
  background: var(--booger-buster);
  color: var(--eerie-black);
  border: none;
  border-radius: 6px;
  padding: 10px 16px;
  font-size: 0.875rem;
  font-weight: 600;
  font-family: 'Archivo', sans-serif;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: #c9e85a;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(211, 240, 115, 0.3);
  }

  &:active {
    transform: translateY(0);
  }
}
</style>
